/**
 * Transport-agnostic API error thrown by shared/api modules so callers
 * (server actions, hooks) never need to import axios directly.
 *
 * `status` is the HTTP status from the response, or `undefined` for
 * network failures and unknown errors. `code` is the transport error code
 * (ECONNRESET, ECONNREFUSED, ...) when the request got no response.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number | undefined,
    public readonly data?: unknown,
    public readonly code?: string,
  ) {
    super(`API error ${status ?? 'unknown'}`);
    this.name = 'ApiError';
  }
}

/**
 * One line for a server log about a failed call, without the request body,
 * headers or tokens that the raw error object would print.
 */
export function describeApiFailure(err: unknown): string {
  if (err instanceof ApiError) {
    return err.status === undefined
      ? `no response from the API (${err.code ?? 'unknown'})`
      : `API status ${err.status}`;
  }
  return err instanceof Error ? `${err.name}: ${err.message}` : 'non-Error value thrown';
}

/** FastAPI-style `{ detail: string }` body, when present. */
export function getApiErrorDetail(data: unknown): string | undefined {
  if (typeof data === 'object' && data !== null && 'detail' in data) {
    const detail = (data as { detail: unknown }).detail;
    return typeof detail === 'string' ? detail : undefined;
  }
  return undefined;
}

/** `detail.code` of a coded `{ detail: { code } }` refusal body, when present. */
export function getApiErrorCode(data: unknown): string | undefined {
  if (typeof data !== 'object' || data === null || !('detail' in data)) return undefined;
  const detail = (data as { detail: unknown }).detail;
  if (typeof detail !== 'object' || detail === null || !('code' in detail)) return undefined;
  const code = (detail as { code: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/**
 * How the API's cap refusal starts. The cap and the per-minute rate limit both
 * answer 429 and the cap carries no code, so this wording is the only way to tell
 * them apart: waiting clears a rate limit but never a cap.
 */
export const CARDINALITY_LIMIT_PREFIX = 'Cardinality limit reached';

export function isCardinalityLimitDetail(data: unknown): boolean {
  return (getApiErrorDetail(data) ?? '').startsWith(CARDINALITY_LIMIT_PREFIX);
}

/** The currency and date an FX rate was missing for. */
export interface FxUnavailable {
  currency: string;
  date: string;
}

const FX_UNAVAILABLE = /^exchange rate unavailable for ([A-Z]{3}) on (\d{4}-\d{2}-\d{2})$/;

/**
 * Read the backend's "no rate for this currency on this date" refusal out of a
 * 422 body, or null for any other shape.
 *
 * A 422 `detail` is polymorphic: a bare string for this refusal and the other
 * service-level guards, an object for batch endpoints, and a list of validation
 * errors when a schema rejects the body. `getApiErrorDetail` already narrows to
 * the string case, so callers can run this against any 422 and fall back to the
 * generic validation copy when it does not match.
 */
export function parseFxUnavailableDetail(data: unknown): FxUnavailable | null {
  const match = FX_UNAVAILABLE.exec(getApiErrorDetail(data) ?? '');
  return match ? { currency: match[1], date: match[2] } : null;
}
